/**
 * Phase 4 Task 14 — RecordingPlayer.
 *
 * HTML5 `<audio>` wrapper that:
 *   1. Fetches `/api/recordings/:id/stream` with the Bearer access token,
 *      reads the `X-PCI-Pause-Segments` header, and converts the audio
 *      response body into a blob URL for the audio element.
 *   2. Skips PCI pause segments client-side via an `ontimeupdate` handler
 *      that fast-forwards `audio.currentTime` past any range whose
 *      `[startOffsetMs, endOffsetMs]` brackets the current playhead.
 *
 * The skip logic is conservative: when `currentTime * 1000` falls inside
 * a pause range, we set `currentTime = endOffsetMs / 1000`. This keeps
 * pause-zero-length and end-of-file segments safe.
 */
import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

export interface PciPauseSegment {
  startOffsetMs: number
  endOffsetMs: number
}

export interface RecordingPlayerProps {
  recordingId: string
}

/**
 * Pure helper exported for testing. Returns the new `currentTime` (sec) the
 * player should jump to, or `null` if no skip is required.
 */
export function nextSkipTimeSec(
  currentTimeSec: number,
  segments: readonly PciPauseSegment[],
): number | null {
  const ms = currentTimeSec * 1000
  for (const seg of segments) {
    if (ms >= seg.startOffsetMs && ms < seg.endOffsetMs) {
      return seg.endOffsetMs / 1000
    }
  }
  return null
}

export function RecordingPlayer({
  recordingId,
}: RecordingPlayerProps): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [segments, setSegments] = useState<PciPauseSegment[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!recordingId) return
    let revoke: string | null = null
    let cancelled = false
    fetch(`/api/recordings/${recordingId}/stream`, {
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`stream ${r.status}`)
        const segHdr = r.headers.get('x-pci-pause-segments')
        if (segHdr) {
          try {
            const parsed = JSON.parse(segHdr) as PciPauseSegment[]
            if (Array.isArray(parsed)) setSegments(parsed)
          } catch {
            // Ignore malformed header — playback still works.
          }
        }
        const blob = await r.blob()
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        revoke = url
        setSrc(url)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'load failed')
      })
    return () => {
      cancelled = true
      if (revoke) URL.revokeObjectURL(revoke)
    }
  }, [recordingId])

  function handleTimeUpdate(): void {
    const audio = audioRef.current
    if (!audio) return
    const skipTo = nextSkipTimeSec(audio.currentTime, segments)
    if (skipTo !== null) audio.currentTime = skipTo
  }

  if (error) {
    return (
      <div data-testid="recording-player-error" className="text-sm text-red-500">
        Failed to load recording: {error}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2" data-testid="recording-player">
      <audio
        ref={audioRef}
        controls
        src={src ?? undefined}
        onTimeUpdate={handleTimeUpdate}
        data-testid="recording-audio"
        data-segment-count={segments.length}
      >
        <track kind="captions" />
      </audio>
      {segments.length > 0 && (
        <div className="text-xs text-gray-400">
          {segments.length} PCI pause segment{segments.length === 1 ? '' : 's'}{' '}
          will be skipped during playback.
        </div>
      )}
    </div>
  )
}
